import React from "react";
import ThreadListModal from "../../pages/Threads/components/ThreadListModal";
import EditableText from "../../components/global/fields/EditableFieldText";
import EditableSelect from "../../components/global/fields/EditableSelect";
import EditableSelectMultiple from "../../components/global/fields/EditableSelectMultiple";
import ColumnStatus from "../../components/global/fields/ColumnStatus";
import SingleDuration from "../../components/global/fields/SingleDurationDatePicker.jsx";

/**
 * Factory para crear columnas estándar de boards
 * @returns {Object} Objeto con funciones para crear diferentes tipos de columnas
 */
export const createStandardColumns = () => ({
  /**
   * Columna ID estándar
   * @param {Object} options - Opciones de configuración
   * @param {number} options.width - Ancho de la columna (default: 70)
   * @param {boolean} options.hide - Si ocultar la columna (default: false)
   * @returns {Object} Definición de columna
   */
  id: (options = {}) => ({
    field: "id",
    headerName: "ID",
    width: options.width || 70,
    headerAlign: "center",
    align: "center",
    hide: options.hide || false,
  }),

  /**
   * Columna de threads estándar
   * @param {Object} options - Opciones de configuración
   * @param {number} options.width - Ancho de la columna (default: 70)
   * @returns {Object} Definición de columna
   */
  threads: (options = {}) => ({
    field: "threads",
    headerName: "Thread",
    width: options.width || 70,
    headerAlign: "center",
    align: "center",
    renderCell: (params) => <ThreadListModal {...params} />,
  }),

  /**
   * Columna de texto editable
   * @param {string} field - Campo de la columna
   * @param {string} headerName - Nombre del header
   * @param {Object} options - Opciones de configuración
   * @param {number} options.width - Ancho de la columna
   * @param {function} options.handleEditResponseChange - Handler para edición
   * @param {function} options.isDisabled - Función para determinar si está deshabilitado
   * @returns {Object} Definición de columna
   */
  editableText: (field, headerName, options = {}) => ({
    field,
    headerName,
    width: options.width || 160,
    headerAlign: "center",
    align: "center",
    renderCell: (params) => (
      <EditableText
        {...params}
        field={field}
        handleEditResponseChange={options.handleEditResponseChange}
        initialValue={params.row[field]}
        disabled={options.isDisabled ? options.isDisabled(params) : false}
      />
    ),
  }),

  /**
   * Columna de select editable
   * @param {string} field - Campo de la columna
   * @param {string} headerName - Nombre del header
   * @param {Array} options.selectOptions - Opciones del select
   * @param {Object} options - Opciones de configuración
   * @returns {Object} Definición de columna
   */
  editableSelect: (field, headerName, options = {}) => ({
    field,
    headerName,
    width: options.width || 160,
    headerAlign: "center",
    align: "center",
    renderCell: (params) => (
      <EditableSelect
        {...params}
        field={field}
        handleEditResponseChange={options.handleEditResponseChange}
        options={options.selectOptions || []}
        initialValue={params.row[field]}
        label={options.label || headerName}
      />
    ),
  }),

  /**
   * Columna de select múltiple editable
   * @param {string} field - Campo de la columna
   * @param {string} headerName - Nombre del header
   * @param {Object} options - Opciones de configuración
   * @returns {Object} Definición de columna
   */
  editableSelectMultiple: (field, headerName, options = {}) => ({
    field,
    headerName,
    width: options.width || 200,
    headerAlign: "center",
    align: "center",
    renderCell: (params) => (
      <EditableSelectMultiple
        {...params}
        field={field}
        handleEditResponseChange={options.handleEditResponseChange}
        options={options.selectOptions || []}
        initialValue={params.row[field]}
        disabled={options.isDisabled ? options.isDisabled(params) : false}
      />
    ),
  }),

  /**
   * Columna de estado con colores
   * @param {string} field - Campo de la columna (default: "status")
   * @param {Object} options - Opciones de configuración
   * @param {Array} options.statusOptions - Opciones de estado
   * @param {number} options.columnPosition - Posición de la columna
   * @returns {Object} Definición de columna
   */
  status: (field = "status", options = {}) => ({
    field,
    headerName: options.headerName || "Status",
    width: options.width || 160,
    cellClassName: "no-padding",
    headerAlign: "center",
    renderCell: (params) => (
      <ColumnStatus
        {...params}
        selectionRow={options.selectionRow || []}
        field={field}
        columnPosition={options.columnPosition || 280}
        handleEditResponseChange={options.handleEditResponseChange}
        statusOption={options.statusOptions || []}
        disabled={options.isDisabled ? options.isDisabled(params) : false}
      />
    ),
  }),

  /**
   * Columna de duración
   * @param {string} field - Campo de la columna
   * @param {string} headerName - Nombre del header
   * @param {Object} options - Opciones de configuración
   * @returns {Object} Definición de columna
   */
  duration: (field, headerName, options = {}) => ({
    field,
    headerName,
    width: options.width || 150,
    headerAlign: "center",
    align: "center",
    renderCell: (params) => (
      <SingleDuration
        {...params}
        selectionRow={options.selectionRow || []}
        fieldKey={field}
        disabled={options.isDisabled ? options.isDisabled(params) : false}
      />
    ),
  }),

  /**
   * Columna de solo lectura (texto simple)
   * @param {string} field - Campo de la columna
   * @param {string} headerName - Nombre del header
   * @param {Object} options - Opciones de configuración
   * @returns {Object} Definición de columna
   */
  readOnly: (field, headerName, options = {}) => ({
    field,
    headerName,
    width: options.width || 160,
    headerAlign: "center",
    align: "center",
    valueGetter: options.valueGetter || ((params) => params.row[field]),
    renderCell: options.renderCell || ((params) => (
      <span style={options.style || {}}>{params.value}</span>
    )),
  }),
});

/**
 * Configuraciones predefinidas de columnas para boards específicos
 */
export const columnPresets = {
  'master-branding': (handlers = {}) => {
    const columns = createStandardColumns();
    return [
      columns.id(),
      columns.threads(),
      columns.editableText("offerID", "Offer ID", { 
        width: 100,
        handleEditResponseChange: handlers.handleEditResponseChange 
      }),
      columns.editableText("offerTitle", "Offer Title", { 
        width: 200,
        handleEditResponseChange: handlers.handleEditResponseChange 
      }),
      columns.status("offerStatus", {
        headerName: "Status",
        statusOptions: handlers.statusOptions || [],
        handleEditResponseChange: handlers.handleEditResponseChange,
        selectionRow: handlers.selectionRow || [],
      }),
    ];
  },

  'tasks-aluna': (handlers = {}) => {
    const columns = createStandardColumns();
    return [
      columns.id(),
      columns.threads(),
      columns.editableText("name", "Name", { 
        width: 200,
        handleEditResponseChange: handlers.handleEditResponseChange 
      }),
      columns.status("status", {
        statusOptions: handlers.statusOptions || [],
        handleEditResponseChange: handlers.handleEditResponseChange,
        selectionRow: handlers.selectionRow || [],
      }),
      columns.editableSelectMultiple("responsibles", "Responsibles", {
        selectOptions: handlers.userOptions || [],
        handleEditResponseChange: handlers.handleEditResponseChange,
      }),
    ];
  },

  'master-performance': (handlers = {}) => {
    const columns = createStandardColumns();
    return [
      columns.id(),
      columns.threads(),
      columns.editableText("offerID", "Offer ID", { 
        width: 100,
        handleEditResponseChange: handlers.handleEditResponseChange 
      }),
      columns.editableText("offerTitle", "Offer Title", { 
        width: 200,
        handleEditResponseChange: handlers.handleEditResponseChange 
      }),
      columns.status("offerStatus", {
        headerName: "Status",
        statusOptions: handlers.statusOptions || [],
        handleEditResponseChange: handlers.handleEditResponseChange,
        selectionRow: handlers.selectionRow || [],
      }),
    ];
  },
};

/**
 * Helper para crear columnas personalizadas basadas en configuración
 * @param {Array} columnConfigs - Array de configuraciones de columnas
 * @param {Object} handlers - Handlers comunes (handleEditResponseChange, etc.)
 * @returns {Array} Array de definiciones de columnas
 */
export const createCustomColumns = (columnConfigs, handlers = {}) => {
  const columns = createStandardColumns();
  
  return columnConfigs.map(config => {
    const { type, field, headerName, options = {} } = config;
    
    // Combinar handlers globales con opciones específicas
    const finalOptions = {
      ...options,
      ...handlers,
    };
    
    switch (type) {
      case 'id':
        return columns.id(finalOptions);
      case 'threads':
        return columns.threads(finalOptions);
      case 'editableText':
        return columns.editableText(field, headerName, finalOptions);
      case 'editableSelect':
        return columns.editableSelect(field, headerName, finalOptions);
      case 'editableSelectMultiple':
        return columns.editableSelectMultiple(field, headerName, finalOptions);
      case 'status':
        return columns.status(field, finalOptions);
      case 'duration':
        return columns.duration(field, headerName, finalOptions);
      case 'readOnly':
        return columns.readOnly(field, headerName, finalOptions);
      default:
        console.warn(`Unknown column type: ${type}`);
        return columns.readOnly(field, headerName, finalOptions);
    }
  });
};